import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react'
import type {
    AudioState,
    ILiveAudioManager,
    LiveAudioManager,
    LiveClient,
    LiveClientState,
    UseLiveClientReturn,
} from '@harmony/live-client'
import {
    useLiveAudioManager as useLiveAudioManagerHook,
    useLiveAudioState as useLiveAudioStateHook,
    useLiveChannelState as useLiveChannelStateHook,
    useLiveClient as useLiveClientHook,
    useLiveClientError as useLiveClientErrorHook,
    useLiveClientState as useLiveClientStateHook,
    useLiveSpaceState as useLiveSpaceStateHook,
    useLiveSpaceSummary as useLiveSpaceSummaryHook,
    useLiveUserDevicesState as useLiveUserDevicesStateHook,
    useLiveUserDeviceState as useLiveUserDeviceStateHook,
} from '@harmony/live-client'
import type {
    AudioErrorResponse,
    LiveChannelState,
    LiveErrorResponse,
    LiveServerMessage,
    LiveSpaceState,
    LiveSpaceSummary,
    LiveUserState,
} from '@harmony/live-types'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useFilteredSortedSpacesByLastAccessed } from 'hooks/useFilteredSortedSpaces'
import { useTownsClient } from 'use-towns-client'
import { env } from 'utils'

import { LiveClientErrorToast } from './LiveClientErrorToast'
import { useLiveStageSounds } from './useLiveStageSounds'

/**
 * Component that uses hooks requiring LiveClient context.
 * Must be rendered inside LiveClientProvider.
 */
function LiveClientEffects() {
    useLiveStageSounds()
    return null
}

type LiveClientContextValue = UseLiveClientReturn & { enabled: boolean }

const LiveClientContext = createContext<LiveClientContextValue | undefined>(undefined)

export function LiveClientProvider({ children }: { children: ReactNode }) {
    const { signerContext } = useTownsClient()
    const { spaceIds } = useFilteredSortedSpacesByLastAccessed()
    const { enableLiveUi } = useFeatureFlags()

    // Get live worker URL from environment if the feature flag is enabled
    const liveWorkerUrl = enableLiveUi
        ? (env.VITE_LIVE_WORKER_URL as string | undefined)
        : undefined

    // set up a constant spaceId, should only change if user leaves the space
    const spaceIdRef = useRef<string | undefined>(undefined)
    const spaceId = useMemo(() => {
        if (spaceIdRef.current === undefined && spaceIds.length > 0) {
            spaceIdRef.current = spaceIds[0]
        } else if (spaceIdRef.current && !spaceIds.includes(spaceIdRef.current)) {
            spaceIdRef.current = spaceIds.at(0)
        }
        return spaceIdRef.current
    }, [spaceIds])

    // Generate or retrieve stable device ID
    const deviceId = useMemo(() => {
        if (typeof window === 'undefined') {
            return undefined
        }

        // use session storage to store the device id so it is unique across tabs
        // this is the fastest way to disambiguate websocket connections from the same browser
        // another option would be to write crazy service worker code, hopefully we don't need to go there.
        // NOTE: if a user "clones their tab" or some shit this will break according to chatgpt
        let id = sessionStorage.getItem('harmony-live-device-id')
        if (!id) {
            id = crypto.randomUUID()
            sessionStorage.setItem('harmony-live-device-id', id)
        }
        return id
    }, [])

    // Derive the domain from the live worker URL for authentication
    // Maps worker URL environment to expected app domain
    const liveWorkerDomain = useMemo(() => {
        if (!liveWorkerUrl) {
            return undefined
        }
        if (liveWorkerUrl.includes('-beta')) {
            return 'app.beta.towns.com'
        }
        if (liveWorkerUrl.includes('-omega') || liveWorkerUrl.includes('live-worker.towns.com')) {
            return 'app.towns.com'
        }
        return 'localhost'
    }, [liveWorkerUrl])

    // Initialize the live client hook with all required options
    const {
        client,
        state,
        connect,
        disconnect,
        updateSubscriptions,
        joinChannel,
        leaveChannel,
        leaveStage,
        joinStage,
        moderateStage,
        ping,
    } = useLiveClientHook({
        liveWorkerUrl,
        wallet: signerContext,
        deviceId,
        spaceId: spaceId,
        domain: liveWorkerDomain,
        // Optional event handlers for debugging
        onReady: () => {
            console.log('[LiveClientProvider ws>] Ready')
        },
        onOpen: () => {
            console.log('[LiveClientProvider ws>] Connected to live worker')
        },
        onClose: (event: CloseEvent) => {
            console.log('[LiveClientProvider ws>] Disconnected from live worker', event)
        },
        onError: (event: Event) => {
            console.error('[LiveClientProvider ws>] Error:', event)
        },
        onMessage: (message: LiveServerMessage) => {
            console.info('[LiveClientProvider ws>] Received message:', message)
        },
    })

    useEffect(() => {
        if (
            !state.ready ||
            state.connectionStatus !== 'connected' ||
            client?.options.spaceId !== spaceId
        ) {
            // bit of a kludge, if you leave the current space the client rolls over
            // this should probably all be handled in the live-client package
            return
        }
        console.log(`[LiveClientProvider ws>] subscribing to ${spaceIds.length} spaces`)
        updateSubscriptions(spaceIds)
    }, [
        updateSubscriptions,
        state.ready,
        state.connectionStatus,
        spaceIds,
        client?.options.spaceId,
        spaceId,
    ])

    return (
        <LiveClientContext.Provider
            value={{
                enabled: liveWorkerUrl !== undefined,
                client,
                state,
                connect,
                disconnect,
                updateSubscriptions,
                joinChannel,
                leaveChannel,
                leaveStage,
                joinStage,
                moderateStage,
                ping,
            }}
        >
            {children}
            <LiveClientEffects />
            <LiveClientErrorToast />
        </LiveClientContext.Provider>
    )
}

/**
 * Hook to access the live client from anywhere in the app
 * @throws Error if used outside of LiveClientProvider
 */
export function useLiveClient() {
    const context = useContext(LiveClientContext)
    if (!context) {
        throw new Error('useLiveClient must be used within LiveClientProvider')
    }
    return context
}

/**
 * Safe version of useLiveClient that returns undefined instead of throwing
 * Use this when you need to gracefully handle being outside the provider
 */
export function useLiveClientSafe() {
    return useContext(LiveClientContext)
}

/**
 * Hook to observe a specific part of the LiveClient state
 */
export function useLiveClientState<T>(selector: (state: LiveClientState) => T): T | undefined {
    const { client } = useLiveClient()
    return useLiveClientStateHook(client, selector)
}

/**
 * Hook to get live state for a specific space
 */
export function useLiveSpaceState(spaceId: string): LiveSpaceState | undefined {
    const { client } = useLiveClient()
    return useLiveSpaceStateHook(client, spaceId)
}

/**
 * Safe version of useLiveSpaceState that handles missing context gracefully
 * Returns undefined if outside provider or feature is disabled
 */
export function useLiveSpaceStateSafe(spaceId: string | undefined): LiveSpaceState | undefined {
    const context = useLiveClientSafe()
    return useLiveSpaceStateHook(context?.enabled ? context.client : undefined, spaceId ?? '')
}

/**
 * Hook to get the live channels
 */
export function useLiveSpaceSummary(spaceId: string): LiveSpaceSummary | undefined {
    const { client } = useLiveClient()
    return useLiveSpaceSummaryHook(client, spaceId)
}

/**
 * Hook to get live state for a specific channel
 */
export function useLiveChannelState(
    spaceId: string | undefined,
    channelId: string | undefined,
): LiveChannelState | undefined {
    const { client } = useLiveClient()
    return useLiveChannelStateHook(client, spaceId, channelId)
}

/**
 * Hook to get the current user's connection state
 */
export function useLiveUserDeviceState(): LiveUserState | undefined {
    const { client } = useLiveClient()
    return useLiveUserDeviceStateHook(client)
}

/**
 * Hook to get the current user's devices connection state
 */
export function useLiveUserDevicesState():
    | { devices: LiveUserState[]; deviceState: LiveUserState | undefined }
    | undefined {
    const { client } = useLiveClient()
    return useLiveUserDevicesStateHook(client)
}

/**
 * Hook to get the last error from the LiveClient
 * Returns the error message string or undefined if no error
 */
export function useLiveClientError(): LiveErrorResponse | AudioErrorResponse | undefined {
    const { client } = useLiveClient()
    return useLiveClientErrorHook(client)
}

/**
 * Hook to manage audio in a live channel
 * Returns audio manager, state, and control methods
 */
export function useLiveAudioState(): AudioState | undefined {
    const { client } = useLiveClient()
    return useLiveAudioStateHook(client)
}

export function useLiveAudioManager(): ILiveAudioManager | undefined {
    const { client } = useLiveClient()
    return useLiveAudioManagerHook(client)
}

// Re-export types for convenience
export type {
    LiveAudioManager as AudioManager,
    AudioState,
    LiveClient,
    LiveClientState,
    UseLiveClientReturn,
}
